import React, { useState, useEffect } from 'react';
import axiosConfig from '../../../../api/axiosConfig';

const TeacherFinancialModal = ({ isOpen, onClose, teacherId, teacherName }) => {
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && teacherId) fetchFinancialData();
  }, [isOpen, teacherId]);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get(`/admin/teachers/${teacherId}/financial-report`);
      setFinancialData(response.data);
    } catch (error) {
      console.error('Finansal veriler alınamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const calculateTotal = (data) =>
    (data.toplam40 || 0) + (data.toplam60 || 0) + (data.toplam80 || 0) + (data.toplam120 || 0);

  const thClass = "text-left px-3 py-2 text-xs font-medium text-gray-500";
  const tdClass = "px-3 py-2 text-sm text-gray-700";
  const money = (v) => `₺${Number(v || 0).toLocaleString('tr-TR')}`;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Finansal Rapor</h3>
            <p className="text-xs text-gray-500 mt-0.5">{teacherName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900"></div>
            </div>
          ) : financialData ? (
            <>
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Mevcut Ay</h4>
                <div className="rounded-md border border-gray-200 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className={thClass}>40 dk</th>
                        <th className={thClass}>60 dk</th>
                        <th className={thClass}>80 dk</th>
                        <th className={thClass}>120 dk</th>
                        <th className={thClass}>Toplam</th>
                        <th className={thClass}>Avans</th>
                        <th className={thClass}>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className={tdClass}>{money(financialData.mevcutAy.toplam40)}</td>
                        <td className={tdClass}>{money(financialData.mevcutAy.toplam60)}</td>
                        <td className={tdClass}>{money(financialData.mevcutAy.toplam80)}</td>
                        <td className={tdClass}>{money(financialData.mevcutAy.toplam120)}</td>
                        <td className={`${tdClass} font-medium text-gray-900`}>
                          {money(calculateTotal(financialData.mevcutAy))}
                        </td>
                        <td className={`${tdClass} text-red-600`}>
                          {money(financialData.mevcutAy.toplamAvans)}
                        </td>
                        <td className={`${tdClass} font-semibold text-gray-900`}>
                          {money(calculateTotal(financialData.mevcutAy) - financialData.mevcutAy.toplamAvans)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Geçmiş Aylar</h4>
                <div className="rounded-md border border-gray-200 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className={thClass}>Ay</th>
                        <th className={thClass}>40 dk</th>
                        <th className={thClass}>60 dk</th>
                        <th className={thClass}>80 dk</th>
                        <th className={thClass}>120 dk</th>
                        <th className={thClass}>Toplam</th>
                        <th className={thClass}>Avans</th>
                        <th className={thClass}>Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {financialData.gecmisAylar.map((ay, index) => {
                        const total = calculateTotal(ay);
                        const net = total - ay.aylikToplamAvans;
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className={`${tdClass} font-medium`}>{ay.ay}</td>
                            <td className={tdClass}>{money(ay.toplam40)}</td>
                            <td className={tdClass}>{money(ay.toplam60)}</td>
                            <td className={tdClass}>{money(ay.toplam80)}</td>
                            <td className={tdClass}>{money(ay.toplam120)}</td>
                            <td className={`${tdClass} font-medium text-gray-900`}>{money(total)}</td>
                            <td className={`${tdClass} text-red-600`}>{money(ay.aylikToplamAvans)}</td>
                            <td className={`${tdClass} font-semibold text-gray-900`}>{money(net)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-400 py-10">Veri yüklenemedi</div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-5 py-3 bg-gray-50/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherFinancialModal;
